/**
 * VehicleMovementComponent - Rapier physics-based vehicle simulation. Integrates
 * vehicle controller with wheel synchronization, acceleration, braking, and steering.
 */
import * as THREE from 'three';

import { PhysicsVectorParam } from '../../physics/PhysicsEngine.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import { BasePawnMovementComponent } from './BasePawnMovementComponent.js';

import type { BasePawnMovementInputState } from './BasePawnMovementComponent.js';
import type { VehicleWheelState } from '../../physics/IPhysicsVehicle.js';
import type { IPhysicsVehicle } from '../../physics/IPhysicsVehicle.js';
import type { VehicleWheelConfig } from '../../physics/IPhysicsVehicle.js';
import type { SceneComponentOptions } from '../SceneComponent.js';


/**
 * Configuration options for vehicle movement
 */
export interface VehicleMovementOptions extends SceneComponentOptions {
  /** Maximum engine power (force applied to powered wheels) */
  maxEnginePower?: number;
  /** Maximum steering angle in radians */
  maxSteeringAngle?: number;
  /** Maximum brake force */
  maxBrakeForce?: number;
  /** Engine acceleration/deceleration rate */
  engineResponseRate?: number;
  /** Steering response rate (how quickly steering changes) */
  steeringResponseRate?: number;
  /** Brake response rate */
  brakeResponseRate?: number;
  /** Wheel configurations */
  wheels?: VehicleWheelConfig[];
  /** Whether to automatically create wheel meshes */
  createWheelMeshes?: boolean;
  /** Wheel mesh material color */
  wheelMaterialColor?: number;
  /** Normalized break input to apply when not possessed */
  autoBreakWhenNotPossessed?: number;
  /** Normalized break input to apply when there's no any input from the player */
  autoStopBreak?: number;
}

/**
 * Input state for the vehicle movement component
 */
export interface VehicleMovementInputState extends BasePawnMovementInputState {
  /** Current brake input (0 to 1) */
  brakeInput: number;
}

export type WheelUpdateDelegate = Delegate<[
  VehicleMovementComponent, // the component that is updating the wheels
  number,                   // the wheel index
  VehicleWheelState,        // the wheel state
], void>;

@EngineClass('VehicleMovementComponent')
export class VehicleMovementComponent extends BasePawnMovementComponent {


  @property()
  public maxEnginePower: number = 35;

  @property()
  public maxSteeringAngle: number = Math.PI / 6;

  @property()
  public maxBrakeForce: number = 1;

  @property()
  public engineResponseRate: number = 8.0;

  @property()
  public steeringResponseRate: number = 12.0;

  @property()
  public brakeResponseRate: number = 15.0;

  @property()
  public wheels: VehicleWheelConfig[] = VehicleMovementComponent.createDefaultWheels();

  @property()
  public createWheelMeshes: boolean = true;

  @property()
  public wheelMaterialColor: number = 0x333333;

  @property()
  public autoBreakWhenNotPossessed: number = 1;

  @property()
  public autoStopBreak: number = 0.5;

  private static createDefaultWheels(): VehicleWheelConfig[] {
    const wheelBaseWidth = 2.6;
    const wheelBaseLength = 3.0;
    const wheelBaseHeight = 0;
    const suspensionRestLength = 0.6;
    const wheelRadius = 0.5;
    const wheelWidth = 0.4;
    const suspensionStiffness = 20;
    const suspensionCompression = 0.83;
    const suspensionRelaxation = 0.88;
    const maxSuspensionForce = 6000.0;
    const maxSuspensionTravel = 5.0;
    const sideFrictionStiffness = 2;
    const frictionSlip = 10.5;

    return [
      // Default 4-wheel configuration
      {
        position: new THREE.Vector3(-wheelBaseWidth / 2, wheelBaseHeight, -wheelBaseLength / 2), // Front left
        radius: wheelRadius,
        width: wheelWidth,
        canSteer: true,
        isPowered: false,
        canBrake: true,
        suspensionRestLength,
        suspensionStiffness,
        suspensionCompression,
        suspensionRelaxation,
        maxSuspensionForce,
        maxSuspensionTravel,
        sideFrictionStiffness,
        frictionSlip,
      },
      {
        position: new THREE.Vector3(wheelBaseWidth / 2, wheelBaseHeight, -wheelBaseLength / 2), // Front right
        radius: wheelRadius,
        width: wheelWidth,
        canSteer: true,
        isPowered: false,
        canBrake: true,
        suspensionRestLength,
        suspensionStiffness,
        suspensionCompression,
        suspensionRelaxation,
        maxSuspensionForce,
        maxSuspensionTravel,
        sideFrictionStiffness,
        frictionSlip,
      },
      {
        position: new THREE.Vector3(-wheelBaseWidth / 2, wheelBaseHeight, wheelBaseLength / 2), // Rear left
        radius: wheelRadius,
        width: wheelWidth,
        canSteer: false,
        isPowered: true,
        canBrake: true,
        suspensionRestLength,
        suspensionStiffness,
        suspensionCompression,
        suspensionRelaxation,
        maxSuspensionForce,
        maxSuspensionTravel,
        sideFrictionStiffness,
        frictionSlip,
      },
      {
        position: new THREE.Vector3(wheelBaseWidth / 2, wheelBaseHeight, wheelBaseLength / 2), // Rear right
        radius: wheelRadius,
        width: wheelWidth,
        canSteer: false,
        isPowered: true,
        canBrake: true,
        suspensionRestLength,
        suspensionStiffness,
        suspensionCompression,
        suspensionRelaxation,
        maxSuspensionForce,
        maxSuspensionTravel,
        sideFrictionStiffness,
        frictionSlip,
      },
    ];
  }

  public readonly onWheelUpdated: WheelUpdateDelegate = new Delegate<[
    VehicleMovementComponent, number, VehicleWheelState,
  ], void>();

  private brakeInput: number = 0;

  // Current smoothed control values
  private currentEngineForce: number = 0;
  private currentBrakeForce: number = 0;
  private currentSteering: number = 0;

  // Physics integration
  private physicsVehicle: IPhysicsVehicle | null = null;
  private wheelMeshes: Map<number, THREE.Object3D> = new Map();

  public override initialize(options?: VehicleMovementOptions): void {
    super.initialize(options);
    this.maxEnginePower = options?.maxEnginePower ?? this.maxEnginePower;
    this.maxSteeringAngle = options?.maxSteeringAngle ?? this.maxSteeringAngle;
    this.maxBrakeForce = options?.maxBrakeForce ?? this.maxBrakeForce;
    this.engineResponseRate = options?.engineResponseRate ?? this.engineResponseRate;
    this.steeringResponseRate = options?.steeringResponseRate ?? this.steeringResponseRate;
    this.brakeResponseRate = options?.brakeResponseRate ?? this.brakeResponseRate;
    this.wheels = options?.wheels ?? this.wheels;
    this.createWheelMeshes = options?.createWheelMeshes ?? this.createWheelMeshes;
    this.wheelMaterialColor = options?.wheelMaterialColor ?? this.wheelMaterialColor;
    this.autoBreakWhenNotPossessed = options?.autoBreakWhenNotPossessed ?? this.autoBreakWhenNotPossessed;
    this.autoStopBreak = options?.autoStopBreak ?? this.autoStopBreak;
  }

  public override getInputState(): VehicleMovementInputState {
    return {
      ...super.getInputState(),
      brakeInput: this.brakeInput,
    };
  }

  public override endPlay(): void {
    this.cleanupVehiclePhysics();
    super.endPlay();
  }

  public getPhysicsVehicle(): IPhysicsVehicle | null {
    return this.physicsVehicle;
  }

  public getCurrentSteeringAngle(): number {
    return this.currentSteering * this.maxSteeringAngle;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.createVehiclePhysics();
    this.updateVehicleControls(deltaTime);
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    this.updateWheels();
  }

  public override jump(strength: number = 1): void {
    this.brakeInput = 1;
  }

  public override stopJump(): void {
    this.brakeInput = 0;
  }

  protected createVehiclePhysics(): void {
    if (this.physicsVehicle) return;

    const physicsEngine = this.getPhysicsEngine();
    if (!physicsEngine) return;

    const actor = this.getActor()!;
    // TOOD: for now the root component is assumed to be the chassis, this may be somewhat limiting
    const chassisComponent = actor.rootComponent;
    if (!(chassisComponent instanceof PrimitiveComponent) || chassisComponent.getMotionType() !== PhysicsMotionType.Dynamic) return;

    // Create vehicle controller through physics engine
    this.physicsVehicle = physicsEngine.createVehicle(this, chassisComponent);
    if (!this.physicsVehicle) return;

    // Setup wheels
    this.wheelMeshes.clear();

    const numWheels = this.wheels.length;
    for (let i = 0; i < numWheels; i++) {
      const wheelConfig = this.wheels[i];

      // Add wheel to vehicle controller
      const wheelIndex = this.physicsVehicle.addWheel(wheelConfig);

      if (this.createWheelMeshes) {
        // Create wheel mesh
        const geometry = new THREE.CylinderGeometry(
          wheelConfig.radius,
          wheelConfig.radius,
          wheelConfig.width,
          16
        );
        geometry.rotateZ(Math.PI * 0.5); // Orient wheel correctly

        const material = new THREE.MeshStandardMaterial({
          color: this.wheelMaterialColor
        });

        const wheelMesh = new THREE.Mesh(geometry, material);
        wheelMesh.castShadow = true;
        wheelMesh.position.copy(wheelConfig.position);
        wheelMesh.setTransient(true);

        // Add wheel to chassis mesh
        chassisComponent.add(wheelMesh);
        this.wheelMeshes.set(wheelIndex, wheelMesh);
      }
    }

    console.log(`VehicleMovementComponent: Created ${this.physicsVehicle.numWheels()} wheels`);
  }

  protected updateVehicleControls(deltaTime: number): void {
    if (!this.physicsVehicle) return;

    const accelerationInput = -this.forwardInput.value;
    let brakeInput = this.brakeInput;
    const steeringInput = -this.rightInput.value;

    if (this.isPlayerControlled()) {
      if (accelerationInput === 0 && brakeInput === 0 && steeringInput === 0) {
        if (this.autoStopBreak > 0) {
          brakeInput += this.autoStopBreak;
        }
      }
    } else {
      if (this.autoBreakWhenNotPossessed > 0) {
        brakeInput += this.autoBreakWhenNotPossessed;
      }
    }

    // Smooth engine force
    const targetEngineForce = accelerationInput * this.maxEnginePower * this.speedModifier;
    this.currentEngineForce = THREE.MathUtils.lerp(
      this.currentEngineForce,
      targetEngineForce,
      this.engineResponseRate * deltaTime
    );

    // Smooth brake force
    const targetBrakeForce = brakeInput * this.maxBrakeForce * this.speedModifier;
    this.currentBrakeForce = THREE.MathUtils.lerp(
      this.currentBrakeForce,
      targetBrakeForce,
      this.brakeResponseRate * deltaTime
    );

    // Smooth steering
    this.currentSteering = THREE.MathUtils.lerp(
      this.currentSteering,
      steeringInput,
      this.steeringResponseRate * deltaTime
    );

    try {
      this.physicsVehicle.setEngineForce(this.currentEngineForce);
      this.physicsVehicle.setBrakeForce(this.currentBrakeForce);
      this.physicsVehicle.setSteering(this.currentSteering * this.maxSteeringAngle);
    } catch (error) {
      console.error(error);
    }
  }

  protected updateWheels(): void {
    if (!this.physicsVehicle) return;

    for (let i = 0; i < this.physicsVehicle.numWheels(); i++) {
      const wheelState = this.physicsVehicle!.getWheelState(i);
      if (!wheelState) continue;

      this.onWheelUpdated.invoke(this, i, wheelState);

      const wheelMesh = this.wheelMeshes.get(i);
      if (wheelMesh) {
        wheelMesh.position.copy(wheelState.relativePosition);
        wheelMesh.quaternion.copy(wheelState.relativeQuaternion);
      }
    }
  }

  protected cleanupVehiclePhysics(): void {
    const physicsEngine = this.getPhysicsEngine();
    if (!physicsEngine) return;

    // Destroy vehicle controller
    if (this.physicsVehicle) {
      this.physicsVehicle.destroy();
      this.physicsVehicle = null;
    }

    this.wheelMeshes.clear();

    console.log('VehicleMovementComponent: Physics cleaned up');
  }

  public flipVehicle(forceFlip: boolean = false): boolean {
    const actor = this.getActor();
    if (!actor) return false;

    const rootComponent = actor.rootComponent;
    if (!rootComponent) return false;

    // Get the vehicle's current up vector
    const vehicleUp = new THREE.Vector3(0, 1, 0);
    const worldQuaternion = new THREE.Quaternion();
    rootComponent.getWorldQuaternion(worldQuaternion);
    vehicleUp.applyQuaternion(worldQuaternion);

    // Check if the vehicle needs flipping (upside down or on its side)
    const worldUp = new THREE.Vector3(0, 1, 0);
    const upDot = vehicleUp.dot(worldUp);

    // Vehicle needs flipping if:
    // - Upside down (dot product < 0)
    // - Tilted more than 60 degrees from upright (dot product < 0.5)
    const needsFlip = upDot < 0.5;

    if (forceFlip || needsFlip) {
      // Get current position and rotation
      const currentPosition = new THREE.Vector3();
      const currentQuaternion = new THREE.Quaternion();
      rootComponent.getWorldPosition(currentPosition);
      rootComponent.getWorldQuaternion(currentQuaternion);

      // Set upright rotation directly, preserving only the Y (yaw) rotation
      const currentEuler = new THREE.Euler();
      currentEuler.setFromQuaternion(currentQuaternion, 'YXZ');

      // Create upright rotation keeping only the Y rotation (yaw)
      const uprightEuler = new THREE.Euler(0, currentEuler.y, 0, 'YXZ');
      const newQuaternion = new THREE.Quaternion();
      newQuaternion.setFromEuler(uprightEuler);

      // Lift the vehicle slightly to avoid collision issues
      currentPosition.y += 2;

      // Apply the new transform
      rootComponent.setWorldPosition(currentPosition);
      rootComponent.setWorldQuaternion(newQuaternion);

      // Reset physics velocity to prevent tumbling
      const physicsEngine = this.getPhysicsEngine();
      if (physicsEngine && rootComponent instanceof PrimitiveComponent) {
        physicsEngine.setVectorParam(rootComponent, PhysicsVectorParam.LinearVelocity, [0, 0, 0]);
        physicsEngine.setVectorParam(rootComponent, PhysicsVectorParam.AngularVelocity, [0, 0, 0]);
      }

      return true;
    }

    return false;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Vehicle';
  }
}
